import { CircleAlert } from 'lucide-react';
import { useId, type Ref, type SelectHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

export interface SelecaoProps extends SelectHTMLAttributes<HTMLSelectElement> {
  rotulo: string;
  opcoes: { valor: string; rotulo: string }[];
  vazio?: string;
  dica?: string;
  erro?: string;
  ref?: Ref<HTMLSelectElement>;
}

/** Lista de opções (select nativo: funciona bem com teclado, leitor de tela e celular). */
export function Selecao({
  rotulo,
  opcoes,
  vazio,
  dica,
  erro,
  required,
  className,
  id,
  ...props
}: SelecaoProps) {
  const idGerado = useId();
  const idCampo = id ?? idGerado;
  const idDica = dica ? `${idCampo}-dica` : undefined;
  const idErro = erro ? `${idCampo}-erro` : undefined;
  return (
    // min-w-0: numa grade, opções longas não alargam a coluna (sem rolagem lateral no celular)
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={idCampo} className="text-sm font-medium text-texto">
        {rotulo}
        {required && (
          <span className="ml-0.5 text-perigo" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <select
        id={idCampo}
        required={required}
        aria-invalid={erro ? true : undefined}
        aria-describedby={[idDica, idErro].filter(Boolean).join(' ') || undefined}
        className={cn(
          'h-11 w-full min-w-0 rounded-lg border border-borda bg-superficie px-3 text-texto',
          erro && 'border-perigo',
          className,
        )}
        {...props}
      >
        {vazio !== undefined && <option value="">{vazio}</option>}
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.rotulo}
          </option>
        ))}
      </select>
      {dica && (
        <p id={idDica} className="text-sm text-texto-suave">
          {dica}
        </p>
      )}
      {erro && (
        <p id={idErro} className="flex items-center gap-1.5 text-sm text-perigo">
          <CircleAlert className="size-4 shrink-0" aria-hidden="true" />
          {erro}
        </p>
      )}
    </div>
  );
}
